/**
 * Application constants for selectors, classes, and configuration values
 * @constant {Object}
 */
const CONSTANTS = {
  SELECTORS: {
    COLOR_SELECTOR: '.color-selector',
    PRODUCT_TILE: '.c-product-tile',
    PRODUCT_LABEL: '.c-product-tile__product-label',
    PRICE_LABEL: '.c-product-tile__price-label',
    PRICE_LABEL_CONTAINER: '.c-product-tile-info',
    PRODUCT_IMAGE: '.image-button__image',
    IMAGE_WRAPPER: '.image-button__image-wrapper',
    LOGO_BADGE: '[data-replaceable-card-logo], .c-product-tile__logo-badge__wrapper',
    PRODUCT_TITLE: '.c-product-tile__product-name span.fixed-utility-s',
    PRODUCT_LINK: '.image-button__link.c-product-tile-decorator__content-wrapper',
    PRICE_CONTAINER: '.c-product-price',
    PRICE_ELEMENT: '.c-product-price__formatted-price',
    DISCOUNTED_PRICE: '.c-product-price__formatted-price--discounted',
    LIST_ITEM: '.o-style-option__list-item',
  },
  CLASSES: {
    CHECKED: 'is-checked',
    HAS_DISCOUNT: 'c-product-price--has-discount',
    LABEL_CONTENT: 'c-product-tile__product-label-content fixed-utility-s',
    DISCOUNTED_PRICE:
      'c-product-price__formatted-price c-product-price__formatted-price--discounted fixed-utility-s one-liner-height-trim',
    SR_ONLY: 'sr-only',
  },
  COLORS: {
    URGENCY: 'c-product-text-color--urgency',
    REGULAR: 'c-product-text-color--regular',
  },
  THRESHOLDS: {
    NEW_PRODUCT_DAYS: 60,
  },
  PATTERNS: {
    SHOPIFY_PRODUCT_ID: /^gid:\/\/shopify\/Product\//g,
  },
};

/**
 * Global Product Data Manager
 * Handles collection-level product data fetching and caching for all product cards
 * Optimized for single API call per page instead of per-card requests
 */
class GlobalProductDataManager {
  constructor() {
    const { endpoint, token } = window.storefrontApi?.createStorefrontClient() || {};
    this.endpoint = endpoint;
    this.token = token;
    this.productCache = new Map();
    this.isInitialized = false;
    this.initializationPromise = null;
  }

  /**
   * Initialize global product data for the entire page
   * Collects all product IDs from swatches and fetches in one API call
   */
  async initialize() {
    if (this.isInitialized) return;
    if (this.initializationPromise) return this.initializationPromise;

    this.initializationPromise = this._performInitialization();
    return this.initializationPromise;
  }

  async _performInitialization() {
    try {
      // Collect all unique product IDs from all swatches on the page
      const swatches = document.querySelectorAll('.color-selector__radio');
      const productIds = new Set();

      swatches.forEach((swatch) => {
        const productId = swatch.dataset.productId;
        if (productId && productId.startsWith('gid://shopify/Product/')) {
          productIds.add(productId);
        }
      });

      if (productIds.size === 0) {
        this.isInitialized = true;
        return;
      }

      const startTime = performance.now();

      // Single API call for all products
      const products = await this.fetchProducts([...productIds]);

      // Cache all products
      products.forEach((product) => {
        if (product?.id) {
          this.productCache.set(product.id, product);
        }
      });

      const endTime = performance.now();
      this.isInitialized = true;

      // Notify all product cards that data is ready
      this._notifyProductCardsReady();
    } catch (error) {
      console.error('Failed to initialize global product data:', error);
      this.isInitialized = true; // Mark as initialized to prevent retries
    }
  }

  /**
   * Notify all product cards that global data is ready
   */
  _notifyProductCardsReady() {
    const productCards = document.querySelectorAll('shared-product-card');
    productCards.forEach((card) => {
      if (card.tryUpgradeInitialImage) {
        card.tryUpgradeInitialImage();
      }

      if (card.onGlobalDataReady) {
        card.onGlobalDataReady();
      }
    });

    // Dispatch custom event for any other listeners
    document.dispatchEvent(
      new CustomEvent('globalProductDataReady', {
        detail: { productCount: this.productCache.size },
      }),
    );
  }

  /**
   * Get product data from cache
   * @param {string} productId - Product ID to retrieve
   * @returns {Object|null} Product data or null if not found
   */
  getProduct(productId) {
    return this.productCache.get(productId) || null;
  }

  /**
   * Check if product data is available
   * @param {string} productId - Product ID to check
   * @returns {boolean} True if product data is cached
   */
  hasProduct(productId) {
    return this.productCache.has(productId);
  }

  /**
   * Get cache statistics
   * @returns {Object} Cache statistics
   */
  getCacheStats() {
    return {
      size: this.productCache.size,
      productIds: Array.from(this.productCache.keys()),
      isInitialized: this.isInitialized,
    };
  }

  /**
   * Check if the API service is properly configured
   * @returns {boolean} True if both endpoint and token are available
   */
  isConfigured() {
    return Boolean(this.endpoint && this.token);
  }

  /**
   * Fetch multiple products from Storefront API using direct GraphQL calls
   * @param {string[]} productIds - Array of Shopify product GIDs
   * @returns {Promise<Object[]>} Array of product objects from API
   * @throws {Error} When API is not configured or request fails
   */
  async fetchProducts(productIds) {
    if (!this.isConfigured()) {
      throw new Error('Storefront API not configured');
    }

    if (!productIds.length) {
      return [];
    }

    try {
      const response = await fetch(this.endpoint, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Shopify-Storefront-Access-Token': this.token,
        },
        body: JSON.stringify({
          query: window.storefrontApi?.PRODUCT_CARD_QUERY,
          variables: { ids: productIds },
        }),
      });

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      const data = await response.json();

      if (data.errors) {
        throw new Error(`GraphQL errors: ${JSON.stringify(data.errors)}`);
      }

      return data.data?.nodes || [];
    } catch (error) {
      console.error('Failed to fetch products', error);
      throw error;
    }
  }
}

/**
 * Legacy StorefrontApiService for backward compatibility
 * Simple wrapper around the global product data manager
 */
class StorefrontApiService {
  constructor() {
    const { endpoint, token } = window.storefrontApi?.createStorefrontClient() || {};
    this.endpoint = endpoint;
    this.token = token;
    this.cache = new Map();
  }

  isConfigured() {
    return Boolean(this.endpoint && this.token);
  }

  async fetchProducts(productIds) {
    if (!this.isConfigured()) {
      throw new Error('Storefront API not configured');
    }

    if (!productIds.length) {
      return [];
    }

    try {
      const response = await fetch(this.endpoint, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Shopify-Storefront-Access-Token': this.token,
        },
        body: JSON.stringify({
          query: window.storefrontApi?.PRODUCT_CARD_QUERY,
          variables: { ids: productIds },
        }),
      });

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      const data = await response.json();

      if (data.errors) {
        throw new Error(`GraphQL errors: ${JSON.stringify(data.errors)}`);
      }

      return data.data?.nodes || [];
    } catch (error) {
      console.error('StorefrontApiService: Failed to fetch products', error);
      throw error;
    }
  }
}

/**
 * Basic utility service for shared DOM operations
 * Provides generic utilities that can be used by multiple components
 */
class DomHelperService {
  /**
   * Safely parse JSON with fallback value
   * @param {string} jsonString - JSON string to parse
   * @param {*} fallback - Fallback value if parsing fails
   * @returns {*} Parsed JSON or fallback value
   */
  static safeParseJSON(jsonString, fallback = {}) {
    try {
      return jsonString ? JSON.parse(jsonString) : fallback;
    } catch (error) {
      console.warn('Failed to parse JSON:', error);
      return fallback;
    }
  }

  /**
   * Announce changes to screen readers for accessibility
   * @param {string} message - Message to announce to screen readers
   */
  static announceToScreenReader(message) {
    const announcement = document.createElement('div');
    announcement.setAttribute('aria-live', 'polite');
    announcement.setAttribute('aria-atomic', 'true');
    announcement.className = 'sr-only';
    announcement.textContent = message;

    document.body.appendChild(announcement);

    setTimeout(() => {
      if (document.body.contains(announcement)) {
        document.body.removeChild(announcement);
      }
    }, 1000);
  }

  /**
   * Extract unique product IDs from swatch elements with validation
   * @param {NodeList} swatches - Collection of swatch elements
   * @returns {string[]} Array of unique Shopify product GIDs
   */
  static extractProductIds(swatches) {
    const productIds = new Set();
    const invalidIds = [];
    const pattern = /^gid:\/\/shopify\/Product\//g;

    swatches.forEach((swatch) => {
      const productId = swatch.dataset?.productId;
      if (productId) {
        if (pattern.test(productId)) {
          productIds.add(productId);
        } else {
          invalidIds.push(productId);
        }
      }
    });

    if (invalidIds.length > 0) {
      console.warn('Found invalid product IDs:', invalidIds);
    }

    return Array.from(productIds);
  }
}

// Export constants globally for custom elements
window.PRODUCT_CARD_CONSTANTS = CONSTANTS;

// Export core services globally for custom elements and legacy code
window.StorefrontApiService = StorefrontApiService;
window.GlobalProductDataManager = GlobalProductDataManager;
window.DomHelperService = DomHelperService;

// Initialize global product data manager
window.globalProductData = new GlobalProductDataManager();

// Initialize global product data when DOM is ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    window.globalProductData.initialize();
  });
} else {
  // DOM is already ready
  window.globalProductData.initialize();
}
